import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { PageHeader, Panel, StatusPill } from "@/components/shared";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import EmailPrefs from "@/components/EmailPrefs";

export default function Privacy() {
  const { user, refresh, setUser } = useAuth();
  const nav = useNavigate();
  const [ints, setInts] = useState([]);
  const [confirm, setConfirm] = useState("");
  const loadInts = () => api.get("/privacy/integrations").then((r) => setInts(r.data));
  useEffect(() => { loadInts(); }, []);
  const toggle = async (v) => { try { await api.put("/privacy/consent", { recruiter_visible: v }); await refresh(); toast.success(v ? "Recruiters can now discover your profile" : "You are hidden from recruiters"); } catch (e) { toast.error(errMsg(e)); } };
  const revoke = async (k) => { try { await api.post(`/privacy/integrations/${k}/revoke`); toast.success("Integration revoked"); loadInts(); } catch (e) { toast.error(errMsg(e)); } };
  const exportData = async () => {
    const { data } = await api.get("/privacy/export");
    const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
    const a = document.createElement("a"); a.href = url; a.download = "jobpedestal-data-export.json"; a.click(); URL.revokeObjectURL(url);
  };
  const del = async () => { try { await api.post("/privacy/delete-account", { confirm }); toast.success("Account deleted"); setUser(false); nav("/"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div>
      <PageHeader eyebrow="You control your data" title="Privacy center" />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 [&>*]:min-w-0">
        {user.role !== "recruiter" && (
          <Panel testid="privacy-consent">
            <h2 className="text-lg font-semibold">Recruiter visibility</h2>
            <p className="mt-1 text-sm text-muted-foreground">When on, recruiters on paid plans can see your headline, level, skills and preferences — never your name, email, phone or resume. They must send a request, and you decide whether to share contact details.</p>
            <label className="mt-4 flex items-center gap-3 text-sm font-medium"><Switch checked={!!user.consent?.recruiter_visible} onCheckedChange={toggle} data-testid="privacy-visibility-switch" />Visible to recruiters</label>
          </Panel>
        )}
        <EmailPrefs />
        <Panel testid="privacy-integrations">
          <h2 className="mb-3 text-lg font-semibold">Connected integrations</h2>
          {ints.map((i) => (
            <div key={i.key} className="flex items-center justify-between border-b py-3 last:border-0">
              <div><p className="text-sm font-medium">{i.name}</p><p className="text-xs text-muted-foreground">{i.connected ? `Connected · ${i.active_sessions ?? 0} active session(s)` : "Not connected"}</p></div>
              {i.status ? <StatusPill status={i.status} /> : <Button size="sm" variant="outline" disabled={!i.connected} onClick={() => revoke(i.key)} data-testid={`privacy-revoke-${i.key}`}>Revoke</Button>}
            </div>
          ))}
        </Panel>
        <Panel testid="privacy-export"><h2 className="text-lg font-semibold">Export your data</h2><p className="mt-1 text-sm text-muted-foreground">Download everything we hold about you as JSON, including decrypted profile, applications, AI history and consent log.</p>
          <Button className="mt-4" variant="outline" onClick={exportData} data-testid="privacy-export-button"><Download className="mr-2 h-4 w-4" />Download export</Button></Panel>
        <Panel testid="privacy-delete"><h2 className="text-lg font-semibold text-red-700">Delete account</h2><p className="mt-1 text-sm text-muted-foreground">Permanently deletes your profile, applications, AI history and saved jobs. Billing records are kept anonymised for accounting. Cancel any active subscription first.</p>
          <div className="mt-4 flex gap-2"><Input placeholder="Type DELETE" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="privacy-delete-confirm-input" />
            <Button variant="destructive" disabled={confirm !== "DELETE"} onClick={del} data-testid="privacy-delete-button"><Trash2 className="mr-2 h-4 w-4" />Delete</Button></div></Panel>
      </div>
    </div>
  );
}
